/**
 * <StatusEffectStrip effects={[...]} onHover={fn} />
 *
 * Renders every active DoT/buff/debuff on a combat target as a compact chip
 * strip. Each chip shows icon + stacks + turns remaining. Hover reveals the
 * full attribution (source, per-turn damage, element).
 *
 * The Combat Overlay places this directly above/below the target name banner
 * so players see at a glance why HP is dropping.
 */
import { ELEMENT_META, normalizeElement } from "../constants/elements";
import {
  Flame, Snowflake, Zap, Skull, Moon, Sun,
  Sparkles, Droplet, Wind as WindIcon, Mountain, Droplets,
} from "lucide-react";

const ELEMENT_ICONS = {
  Fire: Flame, Ice: Snowflake, Lightning: Zap, Poison: Skull,
  Darkness: Moon, Holy: Sun, Arcane: Sparkles,
  Earth: Mountain, Water: Droplet, Wind: WindIcon,
};

// Non-elemental physical DoTs (Bleeding) — default to the drops icon so
// there's a consistent visual language.
const PHYSICAL_ICON = Droplets;


function iconFor(effect) {
  const el = normalizeElement(effect.element);
  if (el && ELEMENT_ICONS[el]) return ELEMENT_ICONS[el];
  return PHYSICAL_ICON;
}

function colorFor(effect) {
  const el = normalizeElement(effect.element);
  return el ? ELEMENT_META[el]?.color : "#B02020";  // physical → blood red
}


export function StatusEffectChip({ effect }) {
  const Icon = iconFor(effect);
  const color = colorFor(effect);
  const stacks = Number(effect.stacks || 1);
  const duration = Number(effect.duration || 0);

  return (
    <span
      data-testid={`status-chip-${(effect.id || effect.name || "effect").toLowerCase()}`}
      title={
        `${effect.name}\n` +
        `Source: ${effect.source || "unknown"}\n` +
        (effect.element ? `Element: ${effect.element}\n` : "") +
        `Damage per turn: ${effect.damage_per_tick} × ${stacks} stacks\n` +
        `Turns remaining: ${duration}`
      }
      className="inline-flex items-center gap-1 border font-mono uppercase tracking-widest text-[9px] px-1.5 py-0.5 backdrop-blur-sm"
      style={{
        borderColor: color,
        color,
        backgroundColor: "rgba(0,0,0,0.55)",
        textShadow: `0 0 6px ${color}60`,
      }}
    >
      <Icon size={11} strokeWidth={1.8} />
      <span className="font-heading">{effect.name}</span>
      {stacks > 1 && <span className="opacity-80">×{stacks}</span>}
      <span className="opacity-60">·{duration}t</span>
    </span>
  );
}


export function StatusEffectStrip({ effects, align = "center" }) {
  const list = (effects || []).filter(e => e && (e.duration ?? 0) > 0);
  if (list.length === 0) return null;
  const justify = align === "start" ? "justify-start"
                 : align === "end"   ? "justify-end"
                 : "justify-center";
  return (
    <div
      className={`flex flex-wrap gap-1 ${justify} px-2`}
      data-testid="status-effect-strip"
    >
      {list.map((e, i) => (
        <StatusEffectChip key={e.id || `${e.name}-${i}`} effect={e} />
      ))}
    </div>
  );
}
